import React, { useEffect, useMemo, useState } from 'react';
import { useAutomationPlaybooks, useAutomationTemplates } from '../../hooks/useAutomations';
import { useToast } from '../../components/Toast';

const AutomationPlaybooks = () => {
    const { playbooks, loading, save, refetch } = useAutomationPlaybooks();
    const { templates } = useAutomationTemplates();
    const [selectedId, setSelectedId] = useState('');
    const [draft, setDraft] = useState(null);
    const { showSuccess, showError } = useToast();

    const selected = useMemo(
        () => playbooks.find((item) => item.id === selectedId) || null,
        [playbooks, selectedId],
    );

    useEffect(() => {
        if (!selectedId && playbooks.length > 0) {
            handleSelect(playbooks[0]);
        }
    }, [playbooks, selectedId]);

    const handleSelect = (playbook) => {
        setSelectedId(playbook.id);
        const cloned = JSON.parse(JSON.stringify(playbook));
        cloned.settings = cloned.settings || {};
        setDraft(cloned);
    };

    const hasTemplate = templates.length > 0;
    const hasEnabledPlaybook = playbooks.some((pb) => pb.enabled);
    const showQuickStart = !hasTemplate || !hasEnabledPlaybook;

    const handleSave = async () => {
        try {
            await save(draft.id, draft);
            showSuccess('Playbook saved');
            await refetch();
        } catch (err) {
            showError(err.message || 'Failed to save playbook');
        }
    };

    const renderTemplateSelect = (value, onChange) => (
        <select className="crm-select" value={value || ''} onChange={(e) => onChange(e.target.value)}>
            <option value="">Select template</option>
            {templates.map((template) => (
                <option key={template.id} value={template.id}>
                    {template.name} ({template.channel})
                </option>
            ))}
        </select>
    );

    return (
        <div className="crm-grid" style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '20px' }}>
            <div className="crm-card crm-card-hover">
                <div className="crm-card-header">
                    <h2 className="crm-card-title">Playbooks</h2>
                </div>
                <div className="crm-card-body">
                    {showQuickStart && (
                        <div className="crm-card crm-card-subtle" style={{ marginBottom: '12px' }}>
                            <div className="crm-card-body">
                                <div style={{ fontWeight: 600, marginBottom: '6px' }}>Quick Start</div>
                                <ol style={{ margin: 0, paddingLeft: '18px', display: 'grid', gap: '6px' }}>
                                    <li>Create Templates.</li>
                                    <li>Enable “New Lead Follow-up” playbook.</li>
                                    <li>Check Outbox + approve.</li>
                                </ol>
                            </div>
                        </div>
                    )}
                    {loading ? (
                        <div className="crm-loading"><div className="crm-spinner" /></div>
                    ) : playbooks.length === 0 ? (
                        <div className="crm-empty-state"><p>No playbooks available.</p></div>
                    ) : (
                        <div className="crm-list">
                            {playbooks.map((playbook) => (
                                <button
                                    key={playbook.id}
                                    type="button"
                                    className="crm-list-row"
                                    onClick={() => handleSelect(playbook)}
                                    style={{
                                        width: '100%',
                                        textAlign: 'left',
                                        background: playbook.id === selectedId ? 'var(--crm-sidebar-hover)' : 'transparent',
                                        border: '1px solid transparent',
                                        padding: '12px',
                                        borderRadius: '12px',
                                        marginBottom: '8px',
                                        color: '#fff',
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                            <div style={{ fontWeight: 600 }}>{playbook.name}</div>
                                            <div style={{ fontSize: '12px', color: 'var(--crm-gray-500)' }}>
                                                {playbook.description}
                                            </div>
                                        </div>
                                        <span className={`crm-status-pill ${playbook.enabled ? 'enabled' : 'disabled'}`}>
                                            {playbook.enabled ? 'Enabled' : 'Disabled'}
                                        </span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="crm-card crm-card-hover">
                <div className="crm-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 className="crm-card-title">Playbook Settings</h2>
                    <button className="crm-btn crm-btn-primary crm-btn-sm" onClick={handleSave} disabled={!draft}>
                        Save
                    </button>
                </div>
                <div className="crm-card-body">
                    {!draft ? (
                        <div className="crm-empty-state"><p>Select a playbook to configure.</p></div>
                    ) : (
                        <div style={{ display: 'grid', gap: '16px' }}>
                            <div className="crm-form-grid">
                                <div className="crm-form-group">
                                    <label className="crm-label">Enabled</label>
                                    <select
                                        className="crm-select"
                                        value={draft.enabled ? 'true' : 'false'}
                                        onChange={(e) => setDraft({ ...draft, enabled: e.target.value === 'true' })}
                                    >
                                        <option value="true">Enabled</option>
                                        <option value="false">Disabled</option>
                                    </select>
                                </div>
                                <div className="crm-form-group">
                                    <label className="crm-label">Name</label>
                                    <input className="crm-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                                </div>
                            </div>

                            {draft.id === 'pb_new_lead_followup' && (
                                <div className="crm-card crm-card-subtle">
                                    <div className="crm-card-header">
                                        <h3 className="crm-card-title">New Lead Follow-up</h3>
                                    </div>
                                    <div className="crm-card-body" style={{ display: 'grid', gap: '12px' }}>
                                        <div className="crm-form-grid">
                                            <div className="crm-form-group">
                                                <label className="crm-label">Call task delay (mins)</label>
                                                <input
                                                    className="crm-input"
                                                    type="number"
                                                    value={draft.settings.callDelayMins || 15}
                                                    onChange={(e) => setDraft({
                                                        ...draft,
                                                        settings: { ...draft.settings, callDelayMins: Number(e.target.value || 0) },
                                                    })}
                                                />
                                            </div>
                                            <div className="crm-form-group">
                                                <label className="crm-label">WhatsApp template</label>
                                                {renderTemplateSelect(draft.settings.whatsappTemplateId, (value) => setDraft({
                                                    ...draft,
                                                    settings: { ...draft.settings, whatsappTemplateId: value },
                                                }))}
                                            </div>
                                            <div className="crm-form-group">
                                                <label className="crm-label">WhatsApp delay (mins)</label>
                                                <input
                                                    className="crm-input"
                                                    type="number"
                                                    value={draft.settings.whatsappDelayMins || 2}
                                                    onChange={(e) => setDraft({
                                                        ...draft,
                                                        settings: { ...draft.settings, whatsappDelayMins: Number(e.target.value || 0) },
                                                    })}
                                                />
                                            </div>
                                        </div>
                                        <div className="crm-section-divider" />
                                        <div className="crm-form-grid">
                                            <div className="crm-form-group">
                                                <label className="crm-label">No contact delay (mins)</label>
                                                <input
                                                    className="crm-input"
                                                    type="number"
                                                    value={draft.settings.noContactDelayMins || 120}
                                                    onChange={(e) => setDraft({
                                                        ...draft,
                                                        settings: { ...draft.settings, noContactDelayMins: Number(e.target.value || 0) },
                                                    })}
                                                />
                                            </div>
                                            <div className="crm-form-group">
                                                <label className="crm-label">No contact tag</label>
                                                <input
                                                    className="crm-input"
                                                    value={draft.settings.noContactTag || 'At Risk'}
                                                    onChange={(e) => setDraft({
                                                        ...draft,
                                                        settings: { ...draft.settings, noContactTag: e.target.value },
                                                    })}
                                                />
                                            </div>
                                            <div className="crm-form-group">
                                                <label className="crm-label">Follow-up task delay (mins)</label>
                                                <input
                                                    className="crm-input"
                                                    type="number"
                                                    value={draft.settings.noContactTaskDelayMins || 15}
                                                    onChange={(e) => setDraft({
                                                        ...draft,
                                                        settings: { ...draft.settings, noContactTaskDelayMins: Number(e.target.value || 0) },
                                                    })}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {draft.id === 'pb_site_visit' && (
                                <div className="crm-card crm-card-subtle">
                                    <div className="crm-card-header">
                                        <h3 className="crm-card-title">Site Visit Workflow</h3>
                                    </div>
                                    <div className="crm-card-body">
                                        <div className="crm-form-grid">
                                            <div className="crm-form-group">
                                                <label className="crm-label">Confirmation template</label>
                                                {renderTemplateSelect(draft.settings.confirmTemplateId, (value) => setDraft({
                                                    ...draft,
                                                    settings: { ...draft.settings, confirmTemplateId: value },
                                                }))}
                                            </div>
                                            <div className="crm-form-group">
                                                <label className="crm-label">Docs task delay (mins)</label>
                                                <input
                                                    className="crm-input"
                                                    type="number"
                                                    value={draft.settings.docsTaskDelayMins || 10}
                                                    onChange={(e) => setDraft({
                                                        ...draft,
                                                        settings: { ...draft.settings, docsTaskDelayMins: Number(e.target.value || 0) },
                                                    })}
                                                />
                                            </div>
                                            <div className="crm-form-group">
                                                <label className="crm-label">Follow-up template</label>
                                                {renderTemplateSelect(draft.settings.followupTemplateId, (value) => setDraft({
                                                    ...draft,
                                                    settings: { ...draft.settings, followupTemplateId: value },
                                                }))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {draft.id === 'pb_no_contact_escalation' && (
                                <div className="crm-card crm-card-subtle">
                                    <div className="crm-card-header">
                                        <h3 className="crm-card-title">No Contact Escalation</h3>
                                    </div>
                                    <div className="crm-card-body">
                                        <div className="crm-form-grid">
                                            <div className="crm-form-group">
                                                <label className="crm-label">Delay (mins)</label>
                                                <input
                                                    className="crm-input"
                                                    type="number"
                                                    value={draft.settings.delayMins || 180}
                                                    onChange={(e) => setDraft({
                                                        ...draft,
                                                        settings: { ...draft.settings, delayMins: Number(e.target.value || 0) },
                                                    })}
                                                />
                                            </div>
                                            <div className="crm-form-group">
                                                <label className="crm-label">Escalation tag</label>
                                                <input
                                                    className="crm-input"
                                                    value={draft.settings.tag || 'Needs Attention'}
                                                    onChange={(e) => setDraft({
                                                        ...draft,
                                                        settings: { ...draft.settings, tag: e.target.value },
                                                    })}
                                                />
                                            </div>
                                            <div className="crm-form-group">
                                                <label className="crm-label">Notify title</label>
                                                <input
                                                    className="crm-input"
                                                    value={draft.settings.notifyTitle || 'Lead needs escalation'}
                                                    onChange={(e) => setDraft({
                                                        ...draft,
                                                        settings: { ...draft.settings, notifyTitle: e.target.value },
                                                    })}
                                                />
                                            </div>
                                            <div className="crm-form-group">
                                                <label className="crm-label">Task delay (mins)</label>
                                                <input
                                                    className="crm-input"
                                                    type="number"
                                                    value={draft.settings.taskDelayMins || 30}
                                                    onChange={(e) => setDraft({
                                                        ...draft,
                                                        settings: { ...draft.settings, taskDelayMins: Number(e.target.value || 0) },
                                                    })}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {draft.id === 'pb_booking_workflow' && (
                                <div className="crm-card crm-card-subtle">
                                    <div className="crm-card-header">
                                        <h3 className="crm-card-title">Booking Workflow</h3>
                                    </div>
                                    <div className="crm-card-body">
                                        <div className="crm-form-grid">
                                            <div className="crm-form-group">
                                                <label className="crm-label">Booking template</label>
                                                {renderTemplateSelect(draft.settings.bookingTemplateId, (value) => setDraft({
                                                    ...draft,
                                                    settings: { ...draft.settings, bookingTemplateId: value },
                                                }))}
                                            </div>
                                            <div className="crm-form-group">
                                                <label className="crm-label">Notify title</label>
                                                <input
                                                    className="crm-input"
                                                    value={draft.settings.notifyTitle || 'Booking workflow started'}
                                                    onChange={(e) => setDraft({
                                                        ...draft,
                                                        settings: { ...draft.settings, notifyTitle: e.target.value },
                                                    })}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {draft.id === 'pb_deal_stage_autopilot' && (
                                <div className="crm-card crm-card-subtle">
                                    <div className="crm-card-header">
                                        <h3 className="crm-card-title">Deal Stage Autopilot</h3>
                                    </div>
                                    <div className="crm-card-body">
                                        <div className="crm-form-grid">
                                            <div className="crm-form-group">
                                                <label className="crm-label">Negotiation task delay (mins)</label>
                                                <input
                                                    className="crm-input"
                                                    type="number"
                                                    value={draft.settings.negotiationTaskDelayMins || 30}
                                                    onChange={(e) => setDraft({
                                                        ...draft,
                                                        settings: { ...draft.settings, negotiationTaskDelayMins: Number(e.target.value || 0) },
                                                    })}
                                                />
                                            </div>
                                            <div className="crm-form-group">
                                                <label className="crm-label">Booking template</label>
                                                {renderTemplateSelect(draft.settings.bookingTemplateId, (value) => setDraft({
                                                    ...draft,
                                                    settings: { ...draft.settings, bookingTemplateId: value },
                                                }))}
                                            </div>
                                            <div className="crm-form-group">
                                                <label className="crm-label">Booking notify title</label>
                                                <input
                                                    className="crm-input"
                                                    value={draft.settings.bookingNotifyTitle || 'Booking stage reached'}
                                                    onChange={(e) => setDraft({
                                                        ...draft,
                                                        settings: { ...draft.settings, bookingNotifyTitle: e.target.value },
                                                    })}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {draft.id === 'pb_lost_lead_winback' && (
                                <div className="crm-card crm-card-subtle">
                                    <div className="crm-card-header">
                                        <h3 className="crm-card-title">Lost Lead Win-back</h3>
                                    </div>
                                    <div className="crm-card-body">
                                        <div className="crm-form-grid">
                                            <div className="crm-form-group">
                                                <label className="crm-label">Delay (mins)</label>
                                                <input
                                                    className="crm-input"
                                                    type="number"
                                                    value={draft.settings.delayMins || 10080}
                                                    onChange={(e) => setDraft({
                                                        ...draft,
                                                        settings: { ...draft.settings, delayMins: Number(e.target.value || 0) },
                                                    })}
                                                />
                                            </div>
                                            <div className="crm-form-group">
                                                <label className="crm-label">Win-back template</label>
                                                {renderTemplateSelect(draft.settings.templateId, (value) => setDraft({
                                                    ...draft,
                                                    settings: { ...draft.settings, templateId: value },
                                                }))}
                                            </div>
                                            <div className="crm-form-group">
                                                <label className="crm-label">Notify title</label>
                                                <input
                                                    className="crm-input"
                                                    value={draft.settings.notifyTitle || 'Win-back follow-up scheduled'}
                                                    onChange={(e) => setDraft({
                                                        ...draft,
                                                        settings: { ...draft.settings, notifyTitle: e.target.value },
                                                    })}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AutomationPlaybooks;
